import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { AcceleratorAreaService } from '../../../accelerator-area.service';
import { AccDeliveryService } from '../../acc-delivery.service';
import { getSetup } from './grid/get-setup';

export interface ProgressBySection {
	courseLevelId: number,
	levelName: string,
	parentLevelName: string | undefined,
	personId: number,
	personFullName: string,
	accVentureId: number,
	ventureName: string,
	isVentureDroppedOut: boolean,
	dueDateUTC: number,
	startUTC: number,
	completedUTC: number | undefined,
	totalRequiredActivitiesCount: number,
	completedActivitiesCount: number,
	updatedUTC: number,
}

@Component({
	selector: 'acc-progress-by-section-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './progress-by-section-view.part.html',
})
export class AccProgressBySectionViewPart {

	private readonly util = inject(UtilityService);
	private readonly pageService = inject(AccDeliveryService);
	private readonly areaService = inject(AcceleratorAreaService);

	public setup = getSetup(this.util);

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});


	public progressBySection = computed<ProgressBySection[]>(() => {
		const data = this.areaService.data();
		if (!data) return [];

		const ventureMap = this.areaService.accVentureMap();

		const accEntPersonMap = this.util.array.toMap(data.entrepreneurs, e => e.personId);

		const courseLevelInfoMap = this.util.array.toMap(data.accSessionCourseLevelsInfo, level => level.courseLevelId);

		const progressBySection: ProgressBySection[] = [];

		for (const enrollment of data.courseEnrollments) {
			for (const levelProgress of enrollment.progress) {
				const levelInfo = courseLevelInfoMap[levelProgress.courseLevelId];
				if (!levelInfo) continue;

				const entrepreneur = accEntPersonMap[enrollment.personId];
				if (!entrepreneur) continue;

				const accVenture = ventureMap[entrepreneur.accVentureId]!; // if entrepreneur exists, venture exists as entrepreneur list is fetched based on ventures

				progressBySection.push({
					courseLevelId: levelProgress.courseLevelId,
					levelName: `${levelInfo.order} : ${levelInfo.title}`,
					parentLevelName: levelInfo.parentLevel ? `${levelInfo.parentLevel.order} : ${levelInfo.parentLevel.title}` : undefined,
					personId: enrollment.personId,
					personFullName: enrollment.personFullName,
					accVentureId: entrepreneur.accVentureId,
					ventureName: accVenture.ventureName,
					isVentureDroppedOut: accVenture.status === 'Dropped Out' || accVenture.status === 'Never Started',
					dueDateUTC: levelInfo.dueDateUTC,
					startUTC: levelProgress.startUTC,
					completedUTC: levelProgress.completedUTC,
					totalRequiredActivitiesCount: levelInfo.totalRequiredActivitiesCount,
					completedActivitiesCount: levelProgress.completedActivitiesCount,
					updatedUTC: data.asOfUTC,
				});
			}
		}

		return progressBySection;
	});
}